/* Copyright (c) 2020-present CloudIO Inc */

import { isNotEmpty } from 'utils/isEmpty';
import { Platform } from '../../platform/Platform';
import { ItemType } from '../../registry/Base.type';
import {
  ActionManager,
  ActionManagerFn,
  AliasType,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionLoopRecords';

export const loopTypes = {
  all: 'All',
  selected: 'Selected',
  dirty: 'Dirty',
} as const;

const ActionLoopRecordsManager: ActionManagerFn<'ActionLoopRecords'> = (
  platform: Platform,
): ActionManager<'ActionLoopRecords'> => {
  return {
    ...baseActionManager(platform, 'ActionLoopRecords'),
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ActionAbort',
          'ActionInvokeCloudFunction',
          'ActionInvokeControllerFunction',
          'ActionLoopDeselect',
          'ActionLoopSelect',
          'ActionSet',
          'ActionSetAttributeError',
          'ActionShowMessage',
          'ActionSleep',
          'ActionSwitch',
        ] as ItemType[]
      ).includes(childType as ItemType),
    defaultValue,
    defaultChildren: ['ActionSet'],
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'ReadOnlyDataStore',
          'DataStore',
          'LocalDataStore',
        ],
      },
      loop: {
        label: 'Loop',
        type: 'radio',
        options: loopTypes,
      },
      matchCondition: {
        label: 'Match Condition',
        type: 'expression',
        returnType: async () => 'boolean',
        helpText: `Specify an expression that should be evaluated to a boolean [e.g. #{dsAlias.attr1} == 'X' && #{dsAlias.attr2} > 1]. This expression
        will be evaluated for each record in the loop and only the matching
        records will be used to execute the child actions. If no expression
        is specified, it will be considered as true`,
      },
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'STORE'
        ? { aliasProp: 'alias', embeddedPageIdProp: 'embeddedPageId' } // TODO: Add embeddedPageId prop
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    name: 'Loop & Update Records',
    getDisplayText: (item) => {
      let label = `${item.props.alias} - ${item.props.loop}`;
      if (isNotEmpty(item.props.matchCondition.value)) {
        label = `${label} (${item.props.matchCondition.value})`;
      }
      return label;
    },
    description: `**Overview**<br/>
The "Loop & Update Records" action is a flow control and batch processing component designed to iterate through a collection of records within a Data Store. Its primary purpose is to execute a defined sequence of logic—such as updating attributes or invoking functions—on a specific subset of data (All, Selected, or Dirty records).<br/><br/>
This component functions as a logic container, strictly managing the execution context for each record in the iteration. It enforces a specific list of allowed child components, ensuring that the operations performed within the loop are compatible with record-level processing (e.g., setting values, conditional switching, or invoking server-side functions). It also features a built-in "Match Condition" filter, allowing builders to apply logic only to records that meet specific criteria without needing nested "If" actions.

**Key Features**<br/>
* **Flexible Iteration Scope:** Provides three distinct looping modes via the loop property:
  * *All:* Iterates through every record in the store.
  * *Selected:* Iterates only through records currently selected in the UI.
  * *Dirty:* Iterates only through records that have been modified but not yet saved.
* **Inline Filtering:** Includes a matchCondition property that evaluates a boolean expression for each record. Child actions are executed only if this condition returns true, effectively combining iteration and filtering in one step.
* **Controlled Logic Environment:** Restricts child components (isChildAllowed) to a specific set of safe actions, including ActionSet (for updates), ActionSwitch (for branching), ActionAbort, and function invocations (ActionInvokeCloudFunction, ActionInvokeControllerFunction).
* **Visual Context:** The getDisplayText method dynamically updates the component's label in the editor to show the target store, the loop type, and any active match condition (e.g., "Employees - Selected (age > 25)"), improving readability.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property identifying the Data Store to iterate over.
* **Loop:** A radio selection determining the scope of iteration (All, Selected, Dirty).
* **Match Condition:** An expression property returning a boolean.
  * *Function:* Evaluated for each record; if true, the child actions run for that record.
  * *Syntax:* Supports standard expression syntax (e.g., #{dsAlias.attr1} == 'X').

**Use Cases**<br/>
* **Bulk Status Updates:** Looping through "Selected" records and using a child ActionSet to change their status to "Approved."
* **Conditional Calculations:** Iterating through "All" records and applying a discount only where the "Total" attribute is greater than 100 via the matchCondition.
* **Validation:** Looping through "Dirty" records to verify data integrity before saving.`,
  };
};
export default ActionLoopRecordsManager;
